import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ProjectDraftSummary } from "@workspace/contracts/projects";
import { Button } from "@workspace/ui/components/button";
import { Edit, ExternalLink, TrashIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { DataTable } from "@/components/data-table/data-table";
import { useDashboardRole } from "@/hooks/use-dashboard-role";
import { useDataTable } from "@/hooks/use-data-table";
import { getError } from "@/utils/error";
import { fDateTime } from "@/utils/format-time";

export const Route = createFileRoute("/_authenticated/projects/drafts/")({
  component: RouteComponent,
  loader: async ({ context: { orpc, queryClient } }) => {
    await queryClient.ensureQueryData(orpc.admin.projectDrafts.list.queryOptions());
  },
});

function RouteComponent() {
  const role = useDashboardRole();
  const queryClient = useQueryClient();
  const { data: drafts = [] } = useQuery(orpc.admin.projectDrafts.list.queryOptions());
  const { mutateAsync: deleteDraft } = useMutation(orpc.admin.projectDrafts.delete.mutationOptions());

  const onDelete = useCallback(
    async (id: string) => {
      try {
        await deleteDraft({ id });
        await queryClient.invalidateQueries({ queryKey: orpc.admin.projectDrafts.list.queryKey() });
        toast.success("Draft deleted");
      } catch (error) {
        toast.error(getError(error, "Failed to delete draft"));
      }
    },
    [deleteDraft, queryClient],
  );

  const columns = useMemo<ColumnDef<ProjectDraftSummary>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Project",
        cell: ({ row }) => (
          <div className="flex flex-col gap-2 px-1 py-2">
            <Link
              params={{ draftId: row.original.id }}
              className="font-medium transition-colors hover:text-primary hover:underline"
              title={row.original.name || row.original.code || "Untitled project"}
              to="/projects/drafts/$draftId"
            >
              {row.original.name || row.original.code || "Untitled project"}
            </Link>
            <div className="flex gap-4 text-xs">
              <Link
                className="text-primary transition-colors hover:text-primary/75 hover:underline"
                to="/projects/drafts/$draftId"
                params={{ draftId: row.original.id }}
              >
                <Edit className="me-1 inline h-3 w-3" />
                Edit
              </Link>
              {role === "admin" && (
                <>
                  <p className="opacity-50">|</p>
                  <button
                    onClick={() => onDelete(row.original.id)}
                    className="text-destructive hover:underline"
                    type="button"
                  >
                    <TrashIcon className="me-1 inline h-3 w-3" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ),
      },
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        cell: ({ row }) => row.original.code || <span className="text-muted-foreground">N/A</span>,
      },
      {
        id: "driveLink",
        accessorKey: "driveLink",
        header: "Drive",
        cell: ({ row }) =>
          row.original.driveLink ? (
            <a
              href={row.original.driveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-500 hover:underline"
            >
              Open <ExternalLink className="size-3" />
            </a>
          ) : (
            <span className="text-muted-foreground">N/A</span>
          ),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ row }) => fDateTime(row.original.createdAt),
      },
    ],
    [onDelete, role],
  );

  const { table } = useDataTable({
    columns,
    data: drafts,
    pageCount: 1,
    getRowId: (originalRow) => originalRow.id,
    enableSorting: false,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-2xl">Project drafts</h1>
        <Button asChild>
          <Link to="/projects/new">New project</Link>
        </Button>
      </div>

      <DataTable table={table} />
    </div>
  );
}
